// Angular Files
import {
 DOCUMENT, Injectable, InjectionToken, computed, inject, signal
} from '@angular/core';

// Teller Online Files
import { RumEvent } from '../rum/rum.models';
import { TellerOnlineRumService } from '../rum/rum.service';

// Component Files
import { GUIDES } from './engagement.config';
import { GuideDefinition, GuideStep } from './engagement.models';
import { isPageBusy } from './engagement-context';
import { readStored, removeStored, writeStored } from './engagement-storage';

export const GUIDE_DEFINITIONS = new InjectionToken<GuideDefinition[]>('GUIDE_DEFINITIONS', {
    factory: () => GUIDES
});

const SEEN_PREFIX = 'to-guide-seen:';

export interface ActiveGuide {
    guide: GuideDefinition;
    index: number;
}

/** Runs in-app guides: one at a time, each recorded as RUM events. */
@Injectable({ providedIn: 'root' })
export class TellerOnlineGuideService {
    private _active = signal<ActiveGuide | null>(null);

    readonly active = this._active.asReadonly();

    readonly step = computed<GuideStep | null>(() => {
        const active = this._active();

        return active ? active.guide.steps[active.index] : null;
    });

    private _definitions = inject(GUIDE_DEFINITIONS);

    private _rum = inject(TellerOnlineRumService);

    private _document = inject(DOCUMENT);

    private _lastShown = '';

    public get definitions(): GuideDefinition[] {
        return this._definitions;
    }

    public hasSeen(guide: GuideDefinition): boolean {
        return readStored<number>(SEEN_PREFIX + guide.id) === guide.version;
    }

    /** Starts a guide; without `force` it only starts if this browser has not seen it. */
    public start(id: string, force = false): boolean {
        const guide = this._definitions.find(g => g.id === id);
        if (!guide || this._active() || (!force && this.hasSeen(guide))) return false;

        this._lastShown = '';
        this._active.set({ guide, index: 0 });
        this._rum.recordEvent(RumEvent.guideStarted, { guideId: guide.id });

        return true;
    }

    /**
     * Auto-starts the first unseen guide of this portal that matches the router path. Backs off
     * when the page is busy (cookie banner, dialog, error); it is offered again next visit.
     */
    public autoStart(path: string, portal: GuideDefinition['portal']): void {
        if (isPageBusy(this._document)) return;

        const guide = this._definitions.find(
            g => g.portal === portal && g.autoStartPath?.test(path) && !this.hasSeen(g));
        if (guide) this.start(guide.id);
    }

    public next(): void {
        const active = this._active();
        if (!active) return;

        if (active.index >= active.guide.steps.length - 1) {
            this._rum.recordEvent(RumEvent.guideCompleted, { guideId: active.guide.id });
            this._finish(active.guide);

            return;
        }
        this._active.set({ ...active, index: active.index + 1 });
    }

    public back(): void {
        const active = this._active();
        if (!active || active.index === 0) return;

        this._active.set({ ...active, index: active.index - 1 });
    }

    /** Moves past a step whose target is absent, without counting it as viewed. */
    public skipMissingStep(direction: 1 | -1): void {
        const active = this._active();
        if (!active) return;

        const index = active.index + direction;
        if (index < 0) return;

        if (index >= active.guide.steps.length) {
            this._rum.recordEvent(RumEvent.guideCompleted, { guideId: active.guide.id });
            this._finish(active.guide);

            return;
        }
        this._active.set({ ...active, index });
    }

    /** Skip or Esc: counts as seen so it does not nag, and records where the user left. */
    public dismiss(): void {
        const active = this._active();
        if (!active) return;

        this._rum.recordEvent(RumEvent.guideDismissed, {
            guideId: active.guide.id,
            step: active.index + 1
        });
        this._finish(active.guide);
    }

    /** Forgets what this browser has seen, for one guide or all of them. */
    public reset(id?: string): void {
        removeStored(SEEN_PREFIX + (id ?? ''));
    }

    private _finish(guide: GuideDefinition): void {
        writeStored(SEEN_PREFIX + guide.id, guide.version);
        this._active.set(null);
    }

    /** Called by the overlay once a step is actually on screen, so skipped steps never count. */
    public stepShown(): void {
        const active = this._active();
        if (!active) return;

        const key = `${active.guide.id}:${active.index}`;
        if (key === this._lastShown) return;

        this._lastShown = key;
        this._rum.recordEvent(RumEvent.guideStepViewed, {
            guideId: active.guide.id,
            step: active.index + 1
        });
    }
}
